import {TABLE_CATEGORY, TableData} from './ProjectBreakdownTable';
import type {ProjectBreakdownTable_viewer$data as ProjectBreakdownTable_viewer} from './__generated__/ProjectBreakdownTable_viewer.graphql';
import Util from '../../../../forecast-app/shared/util/util';
import {IntlFormatters} from 'react-intl';

// A value the viewer is not permitted to see resolves to null, which carries no amount.
const hasValue = (value: number | null | undefined): boolean => value != null && value !== 0;

// Matches financial-service's calculateMargin: a zero revenue gives 0, never NaN or Infinity.
export const calculateMargin = (profit: number, revenue: number): number => (revenue !== 0 ? profit / revenue : 0);

export const getTableData = (
	viewer: ProjectBreakdownTable_viewer,
	formatMessage: IntlFormatters['formatMessage']
): TableData => {
	const roleNameById: Map<number, string> = new Map();
	const expenseCategoryNameById: Map<number, string> = new Map();
	const personById: Map<number, {name: string; profilePictureId: string | undefined}> = new Map();
	const taskNameById: Map<number, string> = new Map();
	const expenseNameById: Map<number, string> = new Map();
	const allocationNameById: Map<number, string> = new Map();

	viewer.company?.roles?.edges?.forEach(edge => {
		if (edge?.node?.name) {
			roleNameById.set(Util.getIdFromBase64String(edge.node.id), edge.node.name);
		}
	});

	viewer.company?.expenseCategories?.edges?.forEach(edge => {
		if (edge?.node?.name) {
			expenseCategoryNameById.set(Util.getIdFromBase64String(edge.node.id), edge.node.name);
		}
	});

	viewer.company?.allPersons?.edges?.forEach(edge => {
		if (edge?.node?.firstName || edge?.node?.lastName) {
			const name = (edge?.node?.firstName || '') + (edge?.node?.lastName !== null ? ' ' + edge?.node?.lastName : '');
			personById.set(Util.getIdFromBase64String(edge.node.id), {
				name: name,
				profilePictureId: edge?.node?.profilePictureId || undefined,
			});
		}
	});

	viewer.project?.tasks?.edges?.forEach(edge => {
		if (edge?.node?.name) {
			taskNameById.set(Util.getIdFromBase64String(edge.node.id), edge.node.name);
		}
	});

	viewer.project?.expenseItems?.edges?.forEach(edge => {
		if (edge?.node?.name) {
			expenseNameById.set(Util.getIdFromBase64String(edge.node.id), edge.node.name);
		}
	});

	viewer.project?.allocations?.edges?.forEach(edge => {
		const allocation = edge?.node;
		if (
			allocation &&
			allocation.startDay &&
			allocation.startMonth &&
			allocation.startYear &&
			allocation.endDay &&
			allocation.endMonth &&
			allocation.endYear
		) {
			const startDate = `${allocation.startDay.toString().padStart(2, '0')}/${allocation.startMonth
				.toString()
				.padStart(2, '0')}/${allocation.startYear}`;
			const endDate = `${allocation.endDay.toString().padStart(2, '0')}/${allocation.endMonth
				.toString()
				.padStart(2, '0')}/${allocation.endYear}`;
			const name = `${startDate} - ${endDate}`;
			allocationNameById.set(Util.getIdFromBase64String(edge.node.id), name);
		}
	});

	const baselineFinancialData = viewer.project?.baselineGroupedFinancialNumbers || [];
	const financialData = viewer.project?.groupedFinancialNumbers || [];
	const tableData: TableData = {
		baseline: [],
		planned: [],
		actualToDate: [],
		forecastToComplete: [],
		totalAtCompletion: [],
	};
	if (!financialData) {
		return tableData;
	}

	baselineFinancialData.forEach(row => {
		if (row) {
			if (
				hasValue(row.baselineMinutes) ||
				hasValue(row.baselineTimeAndExpenses) ||
				hasValue(row.baselineCost) ||
				hasValue(row.baselineProfit) ||
				hasValue(row.baselineMargin) ||
				hasValue(row.baselineRevenue)
			) {
				const isRole = row.roleId && row.roleId > 0;
				const isExpenseCategory = row.expenseCategoryId;
				tableData.baseline.push({
					category: isRole ? TABLE_CATEGORY.TIME : TABLE_CATEGORY.EXPENSES,
					role: isRole
						? {
								id: row.roleId,
								name: roleNameById.get(row.roleId) || formatMessage({id: 'common.no_role'}),
						  }
						: undefined,
					expenseCategory: isExpenseCategory
						? {
								id: row.expenseCategoryId,
								name:
									expenseCategoryNameById.get(row.expenseCategoryId) ||
									formatMessage({id: 'common.no_expense_category'}),
						  }
						: undefined,
					hours: isExpenseCategory ? undefined : row.baselineMinutes || 0,
					revenue: row.baselineRevenue || 0,
					cost: row.baselineCost || 0,
					profit: row.baselineProfit || 0,
					margin: row.baselineMargin || 0,
					valueOfService: row.baselineTimeAndExpenses || 0,
				});
			}
		}
	});

	financialData.forEach(row => {
		if (row) {
			if (
				hasValue(row.scopeTotalMinutes) ||
				hasValue(row.plannedRevenue) ||
				hasValue(row.plannedCost) ||
				hasValue(row.plannedRevenueProfit) ||
				hasValue(row.plannedRevenueMargin) ||
				row.allPlannedTimeAndExpenses
			) {
				const isTask = row.taskId;
				const isExpense = row.expenseId;
				const isAllocation = row.allocationId;

				tableData.planned.push({
					category: isTask
						? TABLE_CATEGORY.TASK_ESTIMATES
						: isExpense
						? TABLE_CATEGORY.EXPENSES
						: TABLE_CATEGORY.ALLOCATIONS,
					person: {
						id: row.personId || 0,
						fullName: personById.get(row.personId || 0)?.name || '',
						profilePictureId: personById.get(row.personId || 0)?.profilePictureId,
					},
					task: isTask
						? {
								id: row.taskId,
								name: taskNameById.get(row.taskId) || '',
						  }
						: undefined,
					expense: isExpense
						? {
								id: row.expenseId,
								name: expenseNameById.get(row.expenseId) || '',
						  }
						: undefined,
					allocation: isAllocation
						? {
								id: row.allocationId,
								name: allocationNameById.get(row.allocationId) || '',
						  }
						: undefined,
					hours: isExpense ? undefined : row.scopeTotalMinutes || 0,
					revenue: row.plannedRevenue || 0,
					cost: row.plannedCost || 0,
					profit: row.plannedRevenueProfit || 0,
					margin: row.plannedRevenueMargin || 0,
					valueOfService: row.allPlannedTimeAndExpenses || 0,
				});
			}

			if (
				hasValue(row.registeredMinutes) ||
				hasValue(row.totalActualRevenueRecognition) ||
				hasValue(row.actualCost) ||
				hasValue(row.actualRevenueProfit) ||
				hasValue(row.actualRevenueMargin) ||
				row.allActualTimeAndExpenses
			) {
				const isTask = row.taskId;
				const isExpense = row.expenseId;
				const isAllocation = row.allocationId;

				tableData.actualToDate.push({
					category: isTask
						? TABLE_CATEGORY.TIME_REGISTRATIONS
						: isExpense
						? TABLE_CATEGORY.EXPENSES
						: TABLE_CATEGORY.ALLOCATIONS,
					person: {
						id: row.personId || 0,
						fullName: personById.get(row.personId || 0)?.name || '',
						profilePictureId: personById.get(row.personId || 0)?.profilePictureId,
					},
					task: isTask
						? {
								id: row.taskId || 0,
								name:
									row.taskId > 0
										? taskNameById.get(row.taskId) || ''
										: row.taskId === -1
										? formatMessage({id: 'common.project_time_registration'})
										: row.taskId === -2
										? formatMessage({id: 'common.revenue_recognition_offset'})
										: '',
						  }
						: undefined,
					expense: isExpense
						? {
								id: row.expenseId,
								name: expenseNameById.get(row.expenseId) || '',
						  }
						: undefined,
					allocation: isAllocation
						? {
								id: row.allocationId,
								name: allocationNameById.get(row.allocationId) || '',
						  }
						: undefined,
					hours: isExpense ? undefined : row.registeredMinutes || 0,
					revenue: row.totalActualRevenueRecognition || 0,
					cost: row.actualCost || 0,
					profit: row.actualRevenueProfit || 0,
					margin: row.actualRevenueMargin || 0,
					valueOfService: row.allActualTimeAndExpenses || 0,
				});
				tableData.totalAtCompletion.push({
					category: isTask
						? TABLE_CATEGORY.TIME_REGISTRATIONS
						: isExpense
						? TABLE_CATEGORY.EXPENSES
						: TABLE_CATEGORY.ALLOCATIONS,
					person: {
						id: row.personId || 0,
						fullName: personById.get(row.personId || 0)?.name || '',
						profilePictureId: personById.get(row.personId || 0)?.profilePictureId,
					},
					task: isTask
						? {
								id: row.taskId || 0,
								name:
									row.taskId > 0
										? taskNameById.get(row.taskId) || ''
										: row.taskId === -1
										? formatMessage({id: 'common.project_time_registration'})
										: row.taskId === -2
										? formatMessage({id: 'common.revenue_recognition_offset'})
										: '',
						  }
						: undefined,
					expense: isExpense
						? {
								id: row.expenseId,
								name: expenseNameById.get(row.expenseId) || '',
						  }
						: undefined,
					allocation: isAllocation
						? {
								id: row.allocationId,
								name: allocationNameById.get(row.allocationId) || '',
						  }
						: undefined,
					hours: isExpense ? undefined : row.registeredMinutes || 0,
					revenue: row.totalActualRevenueRecognition || 0,
					cost: row.actualCost || 0,
					profit: row.actualRevenueProfit || 0,
					margin: row.actualRevenueMargin || 0,
					valueOfService: row.allActualTimeAndExpenses || 0,
				});
			}
		}
	});

	const totalAtCompletionIndexByAllocationId: Map<number, number> = new Map();
	for (let i = 0; i < tableData.totalAtCompletion.length; i++) {
		const allocation = tableData.totalAtCompletion[i].allocation;
		if (allocation) {
			totalAtCompletionIndexByAllocationId.set(allocation.id, i);
		}
	}
	financialData.forEach(row => {
		if (row) {
			if (
				hasValue(row.forecastTimeToComplete) ||
				hasValue(row.totalForecastRevenueToComplete) ||
				hasValue(row.forecastCostToComplete) ||
				hasValue(row.forecastRevenueProfitToComplete) ||
				hasValue(row.forecastRevenueMarginToComplete) ||
				row.allForecastTimeAndExpensesToComplete
			) {
				const isTask = row.taskId;
				const isExpense = row.expenseId;
				const isAllocation = row.allocationId;
				tableData.forecastToComplete.push({
					category: isTask
						? TABLE_CATEGORY.TASK_ESTIMATES
						: isExpense
						? TABLE_CATEGORY.EXPENSES
						: TABLE_CATEGORY.ALLOCATIONS,
					person: {
						id: row.personId || 0,
						fullName: personById.get(row.personId || 0)?.name || '',
						profilePictureId: personById.get(row.personId || 0)?.profilePictureId,
					},
					task: isTask
						? {
								id: row.taskId || 0,
								name:
									row.taskId > 0
										? taskNameById.get(row.taskId) || ''
										: row.taskId === -1
										? formatMessage({id: 'common.project_time_registration'})
										: row.taskId === -2
										? formatMessage({id: 'common.revenue_recognition_offset'})
										: '',
						  }
						: undefined,
					expense: isExpense
						? {
								id: row.expenseId,
								name: expenseNameById.get(row.expenseId) || '',
						  }
						: undefined,
					allocation: isAllocation
						? {
								id: row.allocationId,
								name: allocationNameById.get(row.allocationId) || '',
						  }
						: undefined,
					hours: isExpense ? undefined : row.forecastTimeToComplete || 0,
					revenue: row.totalForecastRevenueToComplete || 0,
					cost: row.forecastCostToComplete || 0,
					profit: row.forecastRevenueProfitToComplete || 0,
					margin: row.forecastRevenueMarginToComplete || 0,
					valueOfService: row.allForecastTimeAndExpensesToComplete || 0,
				});

				const totalAtCompletionIndex = isAllocation
					? totalAtCompletionIndexByAllocationId.get(row.allocationId)
					: undefined;
				if (totalAtCompletionIndex !== undefined) {
					const totalAtCompletionRow = tableData.totalAtCompletion[totalAtCompletionIndex];
					totalAtCompletionRow.hours = (totalAtCompletionRow.hours || 0) + (row.forecastTimeToComplete || 0);
					totalAtCompletionRow.revenue += row.totalForecastRevenueToComplete || 0;
					totalAtCompletionRow.cost += row.forecastCostToComplete || 0;
					totalAtCompletionRow.profit += row.forecastRevenueProfitToComplete || 0;
					// Margin is a ratio, so it is recomputed from the merged totals rather than summed.
					totalAtCompletionRow.margin = calculateMargin(totalAtCompletionRow.profit, totalAtCompletionRow.revenue);
					totalAtCompletionRow.valueOfService += row.allForecastTimeAndExpensesToComplete || 0;
				} else {
					tableData.totalAtCompletion.push({
						category: isTask
							? TABLE_CATEGORY.TASK_ESTIMATES
							: isExpense
							? TABLE_CATEGORY.EXPENSES
							: TABLE_CATEGORY.ALLOCATIONS,
						person: {
							id: row.personId || 0,
							fullName: personById.get(row.personId || 0)?.name || '',
							profilePictureId: personById.get(row.personId || 0)?.profilePictureId,
						},
						task: isTask
							? {
									id: row.taskId || 0,
									name:
										row.taskId > 0
											? taskNameById.get(row.taskId) || ''
											: row.taskId === -1
											? formatMessage({id: 'common.project_time_registration'})
											: row.taskId === -2
											? formatMessage({id: 'common.revenue_recognition_offset'})
											: '',
							  }
							: undefined,
						expense: isExpense
							? {
									id: row.expenseId,
									name: expenseNameById.get(row.expenseId) || '',
							  }
							: undefined,
						allocation: isAllocation
							? {
									id: row.allocationId,
									name: allocationNameById.get(row.allocationId) || '',
							  }
							: undefined,
						hours: isExpense ? undefined : row.forecastTimeToComplete || 0,
						revenue: row.totalForecastRevenueToComplete || 0,
						cost: row.forecastCostToComplete || 0,
						profit: row.forecastRevenueProfitToComplete || 0,
						margin: row.forecastRevenueMarginToComplete || 0,
						valueOfService: row.allForecastTimeAndExpensesToComplete || 0,
					});
				}
			}
		}
	});

	return tableData;
};
